// Sugalarm — push subscriptions data hook (cycle 17).
//
// Owns the small bit of cross-cutting state the Notifications settings
// pane needs: the user's list of push subscriptions, the VAPID public
// key, and the actions (enable/disable/test) the buttons fire.

import { useCallback, useEffect, useState } from "react";

import * as pushApi from "../api/push";
import { getDeviceId } from "../lib/auth";
import {
  currentEndpoint,
  disablePush as browserDisablePush,
  enablePush as browserEnablePush,
} from "../lib/push";
import type {
  ApiError,
  PushSubscription,
  PushTestResponse,
} from "../lib/types";

export interface UsePushSubscriptions {
  subs: PushSubscription[];
  vapidPublicKey: string;
  loading: boolean;
  error: ApiError | null;
  /** Server-side push not configured (no VAPID keys). True iff the
   *  list response carried an empty `vapid_public_key`. */
  serverNotConfigured: boolean;
  /** Endpoint of the current browser, if any subscription exists. */
  currentBrowserEndpoint: string | null;
  refresh: () => Promise<void>;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
  remove: (id: string) => Promise<void>;
  sendTest: () => Promise<PushTestResponse | null>;
}

export function usePushSubscriptions(
  enabledArg: boolean,
): UsePushSubscriptions {
  const [subs, setSubs] = useState<PushSubscription[]>([]);
  const [vapidPublicKey, setVapidPublicKey] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [currentBrowserEndpoint, setCurrentBrowserEndpoint] = useState<
    string | null
  >(null);

  const refresh = useCallback(async () => {
    if (!enabledArg) return;
    setLoading(true);
    setError(null);
    const r = await pushApi.listSubscriptions();
    if (r.ok) {
      setSubs(r.data.subscriptions);
      setVapidPublicKey(r.data.vapid_public_key);
    } else {
      setError(r.error);
    }
    try {
      setCurrentBrowserEndpoint(await currentEndpoint());
    } catch {
      setCurrentBrowserEndpoint(null);
    }
    setLoading(false);
  }, [enabledArg]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    setError(null);
    let key = vapidPublicKey;
    if (!key) {
      const v = await pushApi.getVapid();
      if (!v.ok) {
        setError(v.error);
        throw new Error(v.error.message);
      }
      key = v.data.vapid_public_key;
      setVapidPublicKey(key);
    }
    if (!key) {
      const err: ApiError = {
        kind: "server",
        status: 503,
        message: "Push not configured on the server.",
      };
      setError(err);
      throw new Error(err.message);
    }
    const result = await browserEnablePush(key);
    const r = await pushApi.subscribe({
      endpoint: result.endpoint,
      keys: result.keys,
      user_agent: result.user_agent,
      device_id: getDeviceId() ?? null,
    });
    if (!r.ok) {
      // Roll back the local subscription if the server refused it.
      await browserDisablePush();
      setError(r.error);
      throw new Error(r.error.message);
    }
    await refresh();
  }, [refresh, vapidPublicKey]);

  const disable = useCallback(async () => {
    setError(null);
    const endpoint = await currentEndpoint();
    if (endpoint) {
      const match = subs.find((s) => s.endpoint === endpoint);
      if (match) {
        const r = await pushApi.unsubscribe(match.id);
        if (!r.ok && r.error.status !== 404) {
          setError(r.error);
          throw new Error(r.error.message);
        }
      }
    }
    await browserDisablePush();
    await refresh();
  }, [refresh, subs]);

  const remove = useCallback(
    async (id: string) => {
      setError(null);
      const r = await pushApi.unsubscribe(id);
      if (!r.ok) {
        setError(r.error);
        throw new Error(r.error.message);
      }
      // If the removed row was for *this* browser, also clear the
      // local registration so the UI's "enabled here" pill flips.
      const endpoint = await currentEndpoint();
      const removed = subs.find((s) => s.id === id);
      if (endpoint && removed && removed.endpoint === endpoint) {
        await browserDisablePush();
      }
      await refresh();
    },
    [refresh, subs],
  );

  const sendTest = useCallback(async () => {
    setError(null);
    const r = await pushApi.sendTest();
    if (!r.ok) {
      setError(r.error);
      return null;
    }
    return r.data;
  }, []);

  const serverNotConfigured = !loading && vapidPublicKey === "";

  return {
    subs,
    vapidPublicKey,
    loading,
    error,
    serverNotConfigured,
    currentBrowserEndpoint,
    refresh,
    enable,
    disable,
    remove,
    sendTest,
  };
}
